import { useEffect, useEffectEvent } from "react";
import { TextOrHTML } from "@simplybusiness/mobius";
import { useInterventions } from "@simplybusiness/mobius-interventions";
import classNames from "classnames/dedupe";
import type { FlattenedSectionProps } from ".";
import { useAnswer, useVisibilityMap } from "../../../features/questionnaire";
import { useShouldAnimateQuestions } from "../../../hooks/useShouldAnimateQuestions";
import { getVerticalFromPreambleName, Insurers } from "./Insurers";
import { Question } from "./Question";
import { isQuestion } from "./Question/types";
import {
  getReactInterventionComponent,
  InterventionWrapper,
} from "@simplybusiness/mobius-interventions";

export const Section = ({ ref, ...props }: FlattenedSectionProps) => {
  const {
    id,
    title,
    preamble,
    postamble,
    variant,
    preambleName,
    questions,
    className,
    interventions = [],
  } = props;

  const isStackedSection = variant === "stacked-sections";
  const showInsurers = getVerticalFromPreambleName(preambleName);
  const businessOwner = useAnswer("Business owner");
  const shouldAnimate = useShouldAnimateQuestions();
  const visibilityMap = useVisibilityMap();
  const { getInterventions } = useInterventions();

  const hasVisibleQuestions = questions.some(
    q => visibilityMap[q.name] ?? q.visible,
  );

  const getInterventionsEvent = useEffectEvent(() => {
    if (!isStackedSection && interventions.length > 0) void getInterventions();
  });

  useEffect(() => {
    void getInterventionsEvent();
  }, []);

  if (!hasVisibleQuestions) {
    return null;
  }

  // Custom logic to hide title in US GL journey
  const shouldHideTitle =
    id === "Policyholder information" && businessOwner === "yes";

  const classes = classNames("mobius", "mobius-section", className);

  return (
    <section data-testid="mobius-section" ref={ref} className={classes}>
      {title && !shouldHideTitle && (
        <h2 className="mobius-section__title">{title}</h2>
      )}
      {showInsurers && <Insurers insurersLogosSet={showInsurers} />}
      {preamble && (
        <TextOrHTML
          className="mobius-section__preamble"
          text={preamble}
          textWrapper
        />
      )}
      <div className="mobius-section__question-container">
        {questions.filter(isQuestion).map(question => (
          <Question
            key={question.name}
            {...question}
            shouldAnimate={shouldAnimate}
          />
        ))}
      </div>
      {interventions.map((intervention: string) => {
        const interventionElement = getReactInterventionComponent(intervention);

        if (interventionElement) {
          return (
            <InterventionWrapper key={intervention} name={intervention}>
              {interventionElement}
            </InterventionWrapper>
          );
        }
        return null;
      })}
      {postamble && (
        <TextOrHTML
          htmlClassName={classNames("mobius-section__postamble", {
            "--narrow": isStackedSection,
          })}
          text={postamble}
          textWrapper
        />
      )}
    </section>
  );
};

Section.displayName = "Section";
